import React from "react";
import { Link } from "react-router-dom";
import {
  FileText, ListChecks, BookOpen, ReceiptText, CalendarDays, Bus, ShieldCheck,
  Download, Printer, MessageCircle, Phone,
} from "lucide-react";
import PageHero from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { downloads, admissionDocs, eligibility } from "../mock/pages";
import { site, grades } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { logContactEvent } from "../lib/api";
import useSeo from "../hooks/useSeo";

const icons = { FileText, ListChecks, BookOpen, ReceiptText, CalendarDays, Bus, ShieldCheck };

const docStyles = `
  <style>
    @page { margin: 18mm; }
    body { font-family: Georgia, 'Times New Roman', serif; color: #16241F; line-height: 1.6; }
    h1 { font-size: 22px; margin: 0; color: #0B3B36; }
    .sub { font-size: 11px; letter-spacing: .18em; text-transform: uppercase; color: #C08A2E; margin-top: 6px; }
    hr { border: none; border-top: 1px solid #C08A2E; margin: 18px 0; }
    h2 { font-size: 14px; text-transform: uppercase; letter-spacing: .12em; color: #0B3B36; margin: 26px 0 10px; }
    .row { display: flex; gap: 18px; margin-bottom: 14px; }
    .field { flex: 1; }
    .label { font-size: 11px; text-transform: uppercase; letter-spacing: .1em; color: #5B6B65; }
    .line { border-bottom: 1px solid #9AA8A3; height: 26px; }
    ul { padding-left: 18px; }
    li { margin-bottom: 7px; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    td { padding: 8px 4px; border-bottom: 1px solid #E3DED3; }
    .foot { margin-top: 30px; font-size: 11px; color: #5B6B65; }
    .box { border: 1px solid #C08A2E; padding: 14px 16px; margin-top: 18px; font-size: 12px; }
  </style>
`;

const header = `
  <h1>The KPR Academy</h1>
  <div class="sub">Growing by Giving &middot; CBSE &middot; Arasur, Coimbatore</div>
  <hr />
`;

const footer = `
  <div class="foot">
    Admission hotline &amp; WhatsApp: ${site.hotlines.join(" / ")} &nbsp;|&nbsp; ${site.email}<br/>
    ${site.address}
  </div>
`;

function buildEnquiryForm() {
  const field = (label) => `<div class="field"><div class="label">${label}</div><div class="line"></div></div>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>KPR Academy - Admission Enquiry Form</title>${docStyles}</head><body>
    ${header}
    <h2>Admission Enquiry Form 2026-27</h2>
    <div class="row">${field("Student name")}${field("Date of birth")}</div>
    <div class="row">${field("Grade applying for")}${field("Present school (if any)")}</div>
    <div class="row">${field("Father's name / occupation")}${field("Mother's name / occupation")}</div>
    <div class="row">${field("Mobile number")}${field("Email")}</div>
    <div class="row">${field("Residential address")}</div>
    <div class="row">${field("Transport required (Yes / No) & area")}${field("Blood group")}</div>
    <div class="row">${field("Anything we should know about your child")}</div>
    <h2>Grades offered</h2>
    <p style="font-size:13px">${grades.join(" &middot; ")}</p>
    <div class="box">Carry this filled form along with the documents listed in the Document Checklist to your campus visit. Office hours: Monday to Saturday, 9:00 AM to 4:00 PM.</div>
    <div class="row" style="margin-top:34px">${field("Parent signature")}${field("Date")}</div>
    ${footer}
  </body></html>`;
}

function buildChecklist() {
  return `<!doctype html><html><head><meta charset="utf-8"><title>KPR Academy - Document Checklist</title>${docStyles}</head><body>
    ${header}
    <h2>Admission Document Checklist</h2>
    <ul>${admissionDocs.map((d) => `<li>&#9744; &nbsp; ${d}</li>`).join("")}</ul>
    <h2>Age eligibility</h2>
    <table>${eligibility.map((e) => `<tr><td><strong>${e.grade}</strong></td><td>${e.age}</td></tr>`).join("")}</table>
    <div class="box">Age is reckoned as on 31st March of the academic year. Originals are verified at the school office and returned the same day.</div>
    ${footer}
  </body></html>`;
}

const builders = { "enquiry-form": buildEnquiryForm, checklist: buildChecklist };

export default function DownloadsPage() {
  useSeo({
    title: "Downloads & Forms",
    path: "/downloads",
    description:
      "Download and print the admission enquiry form and document checklist for The KPR Academy, Coimbatore. Request the prospectus, fee structure, academic calendar, transport routes and mandatory public disclosure.",
    keywords:
      "KPR Academy admission form, school prospectus Coimbatore, CBSE fee structure, mandatory public disclosure, school transport routes Coimbatore",
  });
  const { toast } = useToast();

  const openDoc = (key) => {
    const html = builders[key]();
    const win = window.open("", "_blank");
    if (!win) {
      toast({ title: "Pop-up blocked", description: "Allow pop-ups for this site to open the printable document." });
      return;
    }
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 600);
  };

  const saveDoc = (key, name) => {
    const blob = new Blob([builders[key]()], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded", description: `${name} saved to your device.` });
  };

  return (
    <>
      <PageHero
        eyebrow="Downloads"
        title="Forms, checklists and documents"
        sub="Print the enquiry form and checklist before your campus visit. Official documents are shared by the admissions office on request."
        crumbs={[{ label: "Admissions", to: "/admissions" }, { label: "Downloads" }]}
        image="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxzY2hvb2wlMjBsaWJyYXJ5fGVufDB8fHx8MTc4ODQyNDMxNXww&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-24 lg:py-28" style={{ background: "var(--cream)" }}>
        <div className="mx-auto max-w-[1280px] px-5 lg:px-6">
          <SectionHeading eyebrow="Parent resources" title="Everything you need, in one place" />

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {downloads.map((d, i) => {
              const Icon = icons[d.icon];
              const canGenerate = d.kind === "generate";
              return (
                <article
                  key={d.key}
                  data-reveal=""
                  style={{ transitionDelay: `${i * 70}ms`, border: "1px solid rgba(11,59,54,.09)" }}
                  className="flex flex-col rounded-2xl bg-white p-7"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = "0 28px 55px -38px rgba(8,46,41,.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <span
                    className="grid h-12 w-12 place-items-center rounded-xl"
                    style={{
                      background: canGenerate ? "var(--teal-100)" : "var(--sand)",
                      color: canGenerate ? "var(--teal-700)" : "var(--gold)",
                    }}
                  >
                    <Icon size={20} strokeWidth={1.7} />
                  </span>

                  <h3 className="font-display mt-6 text-[1.2rem]" style={{ color: "var(--teal-900)" }}>
                    {d.title}
                  </h3>
                  <p className="mt-2.5 flex-1 text-[0.87rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
                    {d.desc}
                  </p>
                  <p className="mt-4 text-[0.7rem] uppercase tracking-[0.14em]" style={{ color: "var(--gold)" }}>
                    {d.meta}
                  </p>

                  {canGenerate ? (
                    <div className="mt-5 flex gap-2">
                      <button
                        onClick={() => openDoc(d.key)}
                        className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-[0.8rem] font-semibold"
                        style={{ background: "var(--gold)", color: "#fff" }}
                      >
                        <Printer size={15} /> Print
                      </button>
                      <button
                        onClick={() => saveDoc(d.key, d.title)}
                        aria-label={`Download ${d.title}`}
                        className="grid h-11 w-11 place-items-center rounded-full"
                        style={{ border: "1px solid rgba(11,59,54,.16)", color: "var(--teal-800)" }}
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-5 flex gap-2">
                      <a
                        href={`https://wa.me/${site.whatsapp}?text=${encodeURIComponent(
                          `Hello! Please share the ${d.title} for The KPR Academy.`
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() =>
                          logContactEvent({
                            channel: "whatsapp",
                            number: site.whatsapp,
                            display: site.whatsappDisplay,
                            message: `Please share the ${d.title}`,
                            label: `downloads: ${d.title}`,
                          })
                        }
                        className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-[0.8rem] font-semibold"
                        style={{ background: "var(--teal-800)", color: "#FBF7F0" }}
                      >
                        <MessageCircle size={15} /> Request a copy
                      </a>
                      <a
                        href={`tel:${site.hotlines[0].replace(/\s/g, "")}`}
                        onClick={() =>
                          logContactEvent({
                            channel: "call",
                            number: site.hotlines[0].replace(/\s/g, ""),
                            display: site.hotlines[0],
                            label: `downloads: ${d.title}`,
                          })
                        }
                        aria-label="Call the hotline"
                        className="grid h-11 w-11 place-items-center rounded-full"
                        style={{ border: "1px solid rgba(11,59,54,.16)", color: "var(--teal-800)" }}
                      >
                        <Phone size={16} />
                      </a>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <div
            className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl p-8 sm:flex-row sm:items-center"
            style={{ background: "var(--sand)" }}
          >
            <p className="max-w-[620px] text-[0.92rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
              Official PDFs (prospectus, fee structure, academic calendar and mandatory disclosure) are issued by the
              school office so parents always receive the current version. Request a copy and it reaches you the same
              working day.
            </p>
            <Link
              to="/contact"
              className="shrink-0 rounded-full px-7 py-3.5 text-[0.85rem] font-semibold"
              style={{ background: "var(--gold)", color: "#fff" }}
            >
              Contact the office
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
